import { rem } from 'polished'
import styled, { keyframes } from 'styled-components'
import clsx from 'clsx'
import type { ComponentProps } from 'react'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import CartPanel, { CartPanelContent } from '../components/CartPanel'
import { useCartPanelContainerState } from '../hooks/useCartPanelContainerState'

const slideInFromInlineEnd = keyframes`
  from { transform: translateX(var(--cart-panel-inline-end-offset)); }
  to { transform: translateX(0); }
`

const slideOutToInlineEnd = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(var(--cart-panel-inline-end-offset)); }
`

const CartPanelWrapper = styled.div`
  --cart-panel-inline-end-offset: 100%;
  --cart-panel-shadow-x: ${rem(-4)};

  position: absolute;
  inset: 0;
  z-index: 7;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  animation-duration: ${MODAL_TRANSITION_DURATION}ms;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;
  animation-name: ${slideInFromInlineEnd};
  box-shadow: var(--cart-panel-shadow-x) 0 ${rem(24)} rgba(15, 23, 42, 0.15);

  [dir="rtl"] & {
    --cart-panel-inline-end-offset: -100%;
    --cart-panel-shadow-x: ${rem(4)};
  }

  &.closing {
    animation-name: ${slideOutToInlineEnd};
  }
`

interface Props {
  isClosing?: boolean
  onClose: () => void
  presentation?: 'right-panel' | 'embedded'
}

type CartPanelContainerState = ReturnType<typeof useCartPanelContainerState>
type CartPanelContentProps = ComponentProps<typeof CartPanelContent>

function renderCartPanel(
  state: CartPanelContainerState,
  onClose: () => void,
  presentation: NonNullable<Props['presentation']>,
) {
  const {
    items,
    isLoading,
    totalPrice,
    totalValue,
    totalItemCount,
    hasCheckoutReadyItems,
    showReadyItemsOnlyNote,
    loyaltyCalculationRequests,
    handleToggleInclude,
    handleSetDates,
    handleChooseTickets,
    handleChooseDate,
    handleChooseTime,
    handleChooseDifferentTickets,
    handleChooseFlight,
    handleRefreshFlight,
    handleChangeFlight,
    handleViewFlightDetails,
    refreshingFlightKey,
    handleDelete,
    handleCheckout,
    isCheckingOut,
    flightSearchDrawer,
    flightDetailsDrawer,
    agentActionsDisabled,
    isLoggedIn,
  } = state

  const sharedProps: CartPanelContentProps = {
    items,
    isLoading,
    totalPrice,
    totalValue,
    totalItemCount,
    hasCheckoutReadyItems,
    showReadyItemsOnlyNote,
    loyaltyCalculationRequests,
    isLoggedIn,
    isStreaming: agentActionsDisabled,
    onToggleInclude: handleToggleInclude,
    onSetDates: handleSetDates,
    onChooseTickets: handleChooseTickets,
    onChooseDate: handleChooseDate,
    onChooseTime: handleChooseTime,
    onChooseDifferentTickets: handleChooseDifferentTickets,
    onChooseFlight: handleChooseFlight,
    onRefreshFlight: handleRefreshFlight,
    onChangeFlight: handleChangeFlight,
    onViewFlightDetails: handleViewFlightDetails,
    refreshingFlightKey,
    onDelete: handleDelete,
    onCheckout: handleCheckout,
    isCheckoutLoading: isCheckingOut,
    flightSearchDrawer,
    flightDetailsDrawer,
  }

  if (presentation === 'embedded') {
    return <CartPanelContent {...sharedProps} />
  }

  return (
    <CartPanel
      {...sharedProps}
      onClose={onClose}
    />
  )
}

function CartPanelContainer({
  isClosing = false,
  onClose,
  presentation = 'right-panel',
}: Props) {
  const state = useCartPanelContainerState()
  const cartPanel = renderCartPanel(state, onClose, presentation)

  if (presentation === 'embedded') {
    return cartPanel
  }

  return (
    <CartPanelWrapper className={clsx({ closing: isClosing })}>
      {cartPanel}
    </CartPanelWrapper>
  )
}

export default CartPanelContainer
